import { Card, Flex, Heading, Tag, TagProps } from '@applyboard/crystal-ui'

import { ApplicationDetailsFile } from './types'
import { ApplicationDetailCardActions } from './ApplicationDetailCardActions'
import { ApplicationDetailCardDescription } from './ApplicationDetailCardDescription'
import { ApplicationDetailFile } from '../ApplicationDetailFile'
import { ApplicationDetailCardHelper } from './ApplicationDetailCardHelper'
import { ApplicationDetailCardRejectDialogProps } from './ApplicationDetailCardActions/ApplicationDetailCardRejectDialog'
import { ApplicationDetailCardAcceptDialogProps } from './ApplicationDetailCardActions/ApplicationDetailCardAcceptDialog'
import { DecisionResponseType } from '@applyboard/school-applications-types-lib/dist/src/backend/applications-api/resources/applications/student-decisions'
import { DocumentTags } from '../../../../utils/enums'

type ApplicationDetailCardProps = Readonly<{
  title: string
  issuedOn: Date | undefined
  hasOfferConditions: boolean
  file: ApplicationDetailsFile
  activeRecord?: boolean
  status?: DecisionResponseType
  statusChangedOn?: Date
  rejectDialog?: ApplicationDetailCardRejectDialogProps
  onOfferConditionsClick?: () => void
  acceptDialog?: ApplicationDetailCardAcceptDialogProps
  loading?: boolean
  revokedAt?: Date | undefined
  replacedAt?: Date | undefined
}>

export function ApplicationDetailCard(props: ApplicationDetailCardProps) {
  const isRevoked = Boolean(props.revokedAt)
  let tagData:
    | {
        label: TagProps['children']
        intent: TagProps['intent']
      }
    | undefined

  switch (true) {
    case isRevoked:
      tagData = {
        label: 'Revoked',
        intent: 'negative',
      }
      break
    case !props.activeRecord:
      tagData = {
        label: 'Replaced',
        intent: 'magic',
      }
      break
    case props.file.documentTag === DocumentTags.DECLINED_LETTER:
      tagData = {
        label: 'Declined',
        intent: 'negative',
      }
      break
    case props.status === DecisionResponseType.REJECTED:
      tagData = {
        label: 'Rejected',
        intent: 'negative',
      }
      break
    case props.status === DecisionResponseType.ACCEPTED:
      tagData = {
        label: 'Accepted',
        intent: 'positive',
      }
      break
    case props.status === DecisionResponseType.PENDING &&
      props.file.documentTag === DocumentTags.PRE_OFFER_LETTER:
      tagData = {
        label: 'Pending review',
        intent: 'warning',
      }
      break
    default:
      tagData = undefined
      break
  }

  return (
    <Card>
      <Flex direction="column">
        <Flex direction="column" gap={4}>
          <Flex
            gap={3}
            align="center"
            justify={{
              xs: 'between',
              lg: 'start',
            }}
            grow={1}
            wrap
          >
            <Flex gap={3} align="center">
              <Heading variant="titleS" level={3}>
                {props.title}
              </Heading>
            </Flex>
            {tagData ? <Tag intent={tagData.intent}>{tagData.label}</Tag> : null}
          </Flex>

          <Flex>
            <ApplicationDetailCardHelper
              activeRecord={Boolean(props.activeRecord)}
              decisionDocumentTag={props.file.documentTag}
              status={props.status}
              hasCondition={props.hasOfferConditions}
              isRevoked={isRevoked}
            />
          </Flex>

          <Flex
            gap={{ lg: 4 }}
            direction={{ xs: 'column', lg: 'row' }}
            align={{ lg: 'center' }}
            justify={{ xs: 'between', lg: 'between' }}
            pb={{ lg: props.status ? 0 : 4 }}
          >
            <Flex.Item>
              <ApplicationDetailCardDescription
                status={props.status}
                issuedOn={props.issuedOn}
                revokedAt={props.revokedAt}
                statusChangedOn={props.statusChangedOn}
                activeRecord={props.activeRecord}
                replacedAt={props.replacedAt}
              />
            </Flex.Item>
          </Flex>
          <Flex
            direction={{ xs: 'column', lg: 'row' }}
            justify="between"
            align="baseline"
            gap={4}
            pt={4}
          >
            {!isRevoked ? (
              <Flex.Item basis={{ xs: '100%', lg: '43%' }}>
                <ApplicationDetailFile url={props.file.url} name={props.file.name} />
              </Flex.Item>
            ) : null}
            {(props.acceptDialog && props.rejectDialog) || props.onOfferConditionsClick ? (
              <Flex.Item>
                <Flex
                  direction={{ xs: 'column', lg: 'row' }}
                  justify="end"
                  gap={{ xs: 4, lg: 2 }}
                  pb={{ xs: 4, lg: 0 }}
                >
                  <ApplicationDetailCardActions
                    rejectDialog={props.rejectDialog}
                    onOfferConditionsClick={props.onOfferConditionsClick}
                    loading={props.loading}
                    acceptDialog={props.acceptDialog}
                  />
                </Flex>
              </Flex.Item>
            ) : null}
          </Flex>
        </Flex>
      </Flex>
    </Card>
  )
}
